/**
 * Fluent builder for ESC/POS thermal printer commands.
 *
 * Generates a binary command buffer that can be sent directly
 * to a Star TSP143IV (or compatible) USB printer.
 * Text is encoded via CP850 for correct French character output.
 */

import { encodeCP850 } from './cp850';

export class EscPos {
  private buffer: number[] = [];

  /** ESC @ — Initialize printer, reset to default settings. */
  init(): this {
    this.buffer.push(0x1B, 0x40);
    return this;
  }

  /** ESC a 1 — Center alignment. */
  center(): this {
    this.buffer.push(0x1B, 0x61, 0x01);
    return this;
  }

  /** ESC a 0 — Left alignment. */
  left(): this {
    this.buffer.push(0x1B, 0x61, 0x00);
    return this;
  }

  /** ESC E n — Bold on/off. */
  bold(on = true): this {
    this.buffer.push(0x1B, 0x45, on ? 0x01 : 0x00);
    return this;
  }

  /** GS ! n — Double height on/off (width stays normal). */
  doubleHeight(on = true): this {
    this.buffer.push(0x1D, 0x21, on ? 0x01 : 0x00);
    return this;
  }

  /** GS ! n — Double width + double height on/off. */
  doubleSize(on = true): this {
    this.buffer.push(0x1D, 0x21, on ? 0x11 : 0x00);
    return this;
  }

  /** Encode text as CP850 and append with a newline. */
  text(str: string): this {
    const encoded = encodeCP850(str);
    for (let i = 0; i < encoded.length; i++) {
      this.buffer.push(encoded[i]);
    }
    this.buffer.push(0x0A); // LF
    return this;
  }

  /** Alias for text() — encode string + newline. */
  line(str: string): this {
    return this.text(str);
  }

  /** Print an empty line. */
  emptyLine(): this {
    this.buffer.push(0x0A);
    return this;
  }

  /** ESC d n — Feed n lines. */
  feed(lines = 1): this {
    this.buffer.push(0x1B, 0x64, lines);
    return this;
  }

  /**
   * Print an EAN-13 barcode.
   *
   * GS h 80  — Set barcode height to 80 dots
   * GS w 3   — Set barcode width multiplier to 3
   * GS H 2   — Print HRI (human-readable) below barcode
   * GS k 2   — Select EAN-13 format, followed by data + NUL
   */
  barcode(data: string): this {
    // Barcode height
    this.buffer.push(0x1D, 0x68, 80);
    // Barcode width
    this.buffer.push(0x1D, 0x77, 3);
    // HRI position: below
    this.buffer.push(0x1D, 0x48, 2);
    // EAN-13 format
    this.buffer.push(0x1D, 0x6B, 2);
    for (let i = 0; i < data.length; i++) {
      this.buffer.push(data.charCodeAt(i));
    }
    this.buffer.push(0x00); // NUL terminator
    return this;
  }

  /** GS V 0 — Full cut. */
  cut(): this {
    this.buffer.push(0x1D, 0x56, 0x00);
    return this;
  }

  /** ESC d 5 + GS V 1 — Feed 5 lines then partial cut. */
  partialCut(): this {
    this.buffer.push(0x1B, 0x64, 0x05);
    this.buffer.push(0x1D, 0x56, 0x01);
    return this;
  }

  /** Append raw bytes directly to the buffer. */
  raw(bytes: number[]): this {
    for (let i = 0; i < bytes.length; i++) {
      this.buffer.push(bytes[i]);
    }
    return this;
  }

  /** Return the complete command buffer as a Uint8Array. */
  toBytes(): Uint8Array {
    return new Uint8Array(this.buffer);
  }
}
